<template>
  <!--
    A single-select combobox for lists a native <select> cannot carry.

    The topic filter has 420 values. A native <select> with 420 <option>s is not a control an
    operator can use: no search, no counts, and on some platforms a popup that runs off the screen.
    This is the component that makes that filter possible at all, so it is written to look like the
    selects already in QuestionsPage.vue (same height, same border/focus/dark tokens) rather than
    like a widget bolted on beside them.

    THAT SENTENCE IS WHY THE FIELD MOVED TO `.input-field`. It was written when the selects beside it
    were 44px with a hand-rolled `focus:ring-3`; §5.0 made `.input-field` a real global class at
    `h-9`, `SelectInput` renders it, and every one of those neighbours is 36px now. Matching them is
    the same intent, one class instead of fourteen utilities — and it also stops this file painting a
    private focus indicator that beats `main.css:134`'s global ring at (0,3,0), which that file
    already records having been bitten by twice. The label follows `FormField`'s exactly, so a
    `SearchableSelect` and a `TextInput` in one filter bar carry the same label.

    Its API — grouping, counts, `truncatedHint`, the 420-option case — is untouched: the primitives
    README calls this component «unchanged, still correct», and it is.
  -->
  <div ref="root" class="relative" dir="rtl">
    <label v-if="label" :for="inputId" class="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
      {{ label }}
    </label>

    <div class="relative">
      <input
        :id="inputId"
        ref="input"
        type="text"
        role="combobox"
        autocomplete="off"
        spellcheck="false"
        :value="open ? query : selectedLabel"
        :placeholder="open && selectedLabel ? selectedLabel : placeholder"
        :disabled="disabled"
        :aria-label="ariaLabel || label || placeholder"
        :aria-expanded="open ? 'true' : 'false'"
        :aria-controls="listboxId"
        :aria-activedescendant="open && activeOption ? optionDomId(activeOption) : undefined"
        aria-autocomplete="list"
        aria-haspopup="listbox"
        data-testid="searchable-select-input"
        class="input-field pe-16"
        @focus="openList"
        @click="openList"
        @input="onInput"
        @keydown="onKeydown"
      />

      <!-- Trailing controls sit at the end (left, in RTL) so they never overlap the Persian text. -->
      <div class="pointer-events-none absolute inset-y-0 end-2 flex items-center gap-1">
        <button
          v-if="clearable && modelValue !== '' && modelValue !== null && modelValue !== undefined && !disabled"
          type="button"
          tabindex="-1"
          :aria-label="clearLabel"
          :title="clearLabel"
          data-testid="searchable-select-clear"
          class="pointer-events-auto inline-flex h-8 w-8 items-center justify-center rounded-lg text-gray-400 transition-colors hover:text-error-600 dark:text-gray-500 dark:hover:text-error-400"
          @mousedown.prevent
          @click.stop="clear"
        >
          <X class="h-3.5 w-3.5" />
        </button>
        <ChevronDown
          class="h-4 w-4 text-gray-400 transition-transform dark:text-gray-500"
          :class="open ? 'rotate-180' : ''"
          aria-hidden="true"
        />
      </div>
    </div>

    <ul
      v-show="open"
      :id="listboxId"
      ref="listbox"
      role="listbox"
      :aria-label="ariaLabel || label || placeholder"
      data-testid="searchable-select-listbox"
      class="absolute z-40 mt-1 max-h-72 w-full overflow-auto rounded-2xl border border-gray-200 bg-white p-1 shadow-lg dark:border-gray-700 dark:bg-gray-900"
    >
      <!--
        The two grey steps were the wrong way round: `text-gray-400 dark:text-gray-500` put #6b7280 on
        #111827 — 3.67:1, under AA — on both of this list's sentences. ReviewQueuePage's own contrast
        audit measured it the first time this control was mounted there; nine pages already used it and
        none of them measured. Swapped, both sentences clear AA in both themes (4.83:1 light, 6.97:1
        dark), and neither is decoration: one says why the list is empty and the other says the list is
        not all of it.
      -->
      <li v-if="!flatOptions.length" class="px-3 py-3 text-center text-sm text-gray-500 dark:text-gray-400">
        {{ emptyText }}
      </li>

      <li
        v-for="group in visibleGroups"
        :key="group.name || '__ungrouped__'"
        :role="group.name ? 'group' : 'presentation'"
        :aria-label="group.name || undefined"
      >
        <!--
          Chapter headers. aria-hidden because the group's accessible name already comes from
          aria-label; a screen reader announcing the chapter twice per option is noise.
        -->
        <div
          v-if="group.name"
          aria-hidden="true"
          data-testid="searchable-select-group"
          class="sticky top-0 bg-white px-3 pb-1 pt-2 text-xs font-medium text-gray-400 dark:bg-gray-900 dark:text-gray-500"
        >
          {{ group.name }}
        </div>

        <div
          v-for="option in group.options"
          :id="optionDomId(option)"
          :key="option.key"
          role="option"
          data-testid="searchable-select-option"
          :data-key="option.key"
          :aria-selected="String(option.key) === String(modelValue)"
          :aria-disabled="isUnavailable(option) ? 'true' : undefined"
          class="flex cursor-pointer items-center justify-between gap-2 rounded-lg px-3 py-2 text-sm"
          :class="optionClass(option)"
          @mousedown.prevent
          @mousemove="onOptionHover(option)"
          @click="select(option)"
        >
          <!-- §8.5: `truncate` always carries a `:title`. This is the one string that says WHICH
               topic a 420-option list is offering, and a Persian subject chain clips early. -->
          <span class="truncate text-start" :title="option.label">{{ option.label }}</span>
          <span
            v-if="showCounts && option.count !== null && option.count !== undefined"
            data-testid="searchable-select-count"
            class="shrink-0 rounded-full px-2 py-0.5 text-xs font-medium"
            :class="
              isUnavailable(option)
                ? 'bg-gray-100 text-gray-400 dark:bg-gray-800 dark:text-gray-600'
                : 'bg-brand-100 text-brand-700 dark:bg-brand-900/30 dark:text-brand-300'
            "
            >{{ faNumber(option.count) }}</span
          >
        </div>
      </li>

      <li
        v-if="truncatedCount > 0"
        data-testid="searchable-select-truncated"
        class="px-3 py-2 text-center text-sm text-gray-500 dark:text-gray-400"
      >
        {{ truncatedHint }}
      </li>
    </ul>
  </div>
</template>

<script>
// Named exports live in a plain <script> block so the normalisation can be unit-tested on its own
// and reused by a caller that needs to pre-filter. <script setup> below can read these bindings.
//
// The default export carries only the component's NAME. With a plain <script> beside `<script setup>`
// the filename-inferred name does not survive, so `findAllComponents({ name })`, devtools and warning
// traces all saw an anonymous component. Written here and not as `defineOptions()`, which is a compiler
// macro this repo's eslint config does not know — and the production build runs eslint. `Checkbox.vue`
// carries `inheritAttrs` the same way.
export default { name: 'SearchableSelect' }

const FOLD = {
  // Every yeh an operator's keyboard might produce collapses to the Persian yeh (U+06CC).
  // «ریاضی» spelled with the Arabic yeh U+064A is a different string to String.includes, and a box
  // that only knows U+06CC returns nothing for it — which reads as a broken search box, not as a
  // spelling difference. Windows Arabic layouts and pasted web text produce U+064A constantly.
  'ي': 'ی', // ARABIC YEH
  'ى': 'ی', // ARABIC ALEF MAKSURA
  'ے': 'ی', // YEH BARREE
  'ئ': 'ی', // YEH WITH HAMZA ABOVE
  // Same story for kaf: U+0643 (Arabic) vs U+06A9 (Persian keheh).
  'ك': 'ک',
  'ڪ': 'ک',
  // Alef forms fold together so «آ» / «أ» / «إ» all match a plain alef.
  'آ': 'ا',
  'أ': 'ا',
  'إ': 'ا',
  'ٱ': 'ا',
  // Teh marbuta and heh variants fold to plain heh.
  'ة': 'ه',
  'ۀ': 'ه',
  'ھ': 'ه',
  'ؤ': 'و', // WAW WITH HAMZA ABOVE
}

// Zero-width joiners/marks (U+200B..U+200F), the bidi isolates a paste can carry (U+2066..U+2069),
// the BOM, tatweel, and the standalone hamza. Written as escapes on purpose: every character in
// this class is invisible in an editor, so a literal one here would be unreviewable. ZWNJ matters
// most — «می‌شود» and «میشود» differ by a character nobody can see.
const STRIPPED = /[\u200B-\u200F\u2066-\u2069\uFEFF\u0640\u0621]/g
// Harakat / superscript alef — decorative, never typed into a filter box.
const DIACRITICS = /[\u064B-\u065F\u0670\u06D6-\u06ED]/g

/**
 * Fold a Persian/Arabic/Latin string down to the one spelling the matcher compares against.
 * Digits become ASCII, so «۱۰» and «١٠» and «10» are the same needle.
 */
export function normalizePersian(value) {
  if (value === null || value === undefined) {
    return ''
  }
  let s = String(value)
  if (typeof s.normalize === 'function') {
    s = s.normalize('NFKC')
  }
  s = s.replace(STRIPPED, '').replace(DIACRITICS, '')
  let out = ''
  for (const ch of s) {
    const code = ch.codePointAt(0)
    if (code >= 0x06f0 && code <= 0x06f9) {
      out += String.fromCharCode(code - 0x06f0 + 48) // Persian digits
    } else if (code >= 0x0660 && code <= 0x0669) {
      out += String.fromCharCode(code - 0x0660 + 48) // Arabic-Indic digits
    } else {
      out += FOLD[ch] || ch
    }
  }
  return out.toLowerCase().replace(/\s+/g, ' ').trim()
}

/**
 * Substring match under Persian folding. The second pass ignores spacing entirely, so a topic
 * written with a ZWNJ («جمع‌بندی») is found whether the operator types it joined or with a space.
 */
export function persianIncludes(haystack, needle) {
  const n = normalizePersian(needle)
  if (!n) {
    return true
  }
  const h = normalizePersian(haystack)
  if (h.includes(n)) {
    return true
  }
  return h.replace(/ /g, '').includes(n.replace(/ /g, ''))
}

let uid = 0
export function nextSearchableSelectId() {
  uid += 1
  return `searchable-select-${uid}`
}
</script>

<script setup>
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { ChevronDown, X } from 'lucide-vue-next'

const props = defineProps({
  /** Selected key. '' means "nothing selected", matching the '' the page filters already use. */
  modelValue: { type: [String, Number, null], default: '' },
  /** [{ key, label, count?, group?, disabled? }] — `group` is the chapter a topic belongs to. */
  options: { type: Array, default: () => [] },
  placeholder: { type: String, default: 'جست‌وجو…' },
  label: { type: String, default: '' },
  ariaLabel: { type: String, default: '' },
  disabled: { type: Boolean, default: false },
  clearable: { type: Boolean, default: true },
  clearLabel: { type: String, default: 'پاک‌کردن' },
  emptyText: { type: String, default: 'گزینه‌ای یافت نشد.' },
  showCounts: { type: Boolean, default: true },
  /**
   * What to do with an option the facets say has zero rows.
   *
   * Default is to SHOW it, greyed and unselectable. The bug this control exists for is «ریاضی‌فیزیک
   * پایهٔ دوازدهم» offering «زمین‌شناسی» — an option that leads to an empty list. Dropping such an
   * option silently trades that bug for a quieter one: the operator cannot tell «this topic does not
   * exist» from «this topic has nothing under your current filters», and goes looking for a bank
   * outage. A dimmed row with a ۰ badge answers that question on sight and still cannot be chosen,
   * which is what "not offerable" actually requires. A caller whose list would be mostly zeroes can
   * pass hide-unavailable and get the shorter list instead.
   */
  hideUnavailable: { type: Boolean, default: false },
  /** Cap on rendered rows; the rest are reachable by typing. 420 topics re-render on every keystroke. */
  maxVisible: { type: Number, default: 150 },
})

const emit = defineEmits(['update:modelValue', 'change', 'open', 'close'])

const root = ref(null)
const input = ref(null)
const listbox = ref(null)
const open = ref(false)
const query = ref('')
const activeKey = ref(null)

const componentId = nextSearchableSelectId()
const inputId = `${componentId}-input`
const listboxId = `${componentId}-listbox`
const optionDomId = (option) => `${componentId}-option-${encodeURIComponent(String(option.key))}`

const faNumber = (n) => new Intl.NumberFormat('fa-IR').format(Number(n) || 0)

const isUnavailable = (option) => option.disabled === true || option.count === 0

const selectedOption = computed(() =>
  props.options.find((o) => String(o.key) === String(props.modelValue ?? '')),
)
// Falling back to the raw key keeps a stale selection visible instead of blanking the box, so the
// operator can see what to clear when the facets stop offering what they had chosen.
const selectedLabel = computed(() => {
  if (props.modelValue === '' || props.modelValue === null || props.modelValue === undefined) {
    return ''
  }
  return selectedOption.value ? selectedOption.value.label : String(props.modelValue)
})

const matched = computed(() => {
  const pool = props.hideUnavailable ? props.options.filter((o) => !isUnavailable(o)) : props.options
  if (!query.value.trim()) {
    return pool
  }
  return pool.filter((o) => persianIncludes(o.label, query.value) || persianIncludes(o.key, query.value))
})

const truncatedCount = computed(() => Math.max(0, matched.value.length - props.maxVisible))
const truncatedHint = computed(() => `و ${faNumber(truncatedCount.value)} گزینهٔ دیگر — برای یافتن، بنویسید.`)

/**
 * The rendered rows — capped, but never without the current selection.
 *
 * `maxVisible` keeps a 420-row listbox from rendering 420 DOM nodes, and the cap is fine until the
 * operator's own choice sits past it. Then the box opens on a list that does not contain what is
 * selected: nothing is highlighted, `aria-activedescendant` is absent on an open combobox, Enter is a
 * silent no-op, and ArrowDown jumps to row 1 instead of moving from the selection — so the keyboard
 * path silently stops working for exactly the long lists this control exists to handle.
 *
 * The selection is therefore always in `shown`, appended when the slice missed it. It is appended rather
 * than prepended so the visible order still reflects the list's own order for everything else.
 */
const shown = computed(() => {
  const head = matched.value.slice(0, props.maxVisible)
  if (head.length === matched.value.length) return head

  const current = String(props.modelValue ?? '')
  if (current === '') return head

  const selected = matched.value.find((o) => String(o.key) === current)

  return selected && !head.some((o) => String(o.key) === current) ? [...head, selected] : head
})

/** Groups in first-seen order; ungrouped options keep their place under an unnamed bucket. */
const visibleGroups = computed(() => {
  const groups = []
  const byName = new Map()
  for (const option of shown.value) {
    const name = option.group || ''
    let bucket = byName.get(name)
    if (!bucket) {
      bucket = { name, options: [] }
      byName.set(name, bucket)
      groups.push(bucket)
    }
    bucket.options.push(option)
  }
  return groups
})

/** Render order, flattened — this is what the arrow keys walk. */
const flatOptions = computed(() => visibleGroups.value.flatMap((g) => g.options))
const selectableOptions = computed(() => flatOptions.value.filter((o) => !isUnavailable(o)))
const activeOption = computed(() => flatOptions.value.find((o) => String(o.key) === String(activeKey.value)))

const optionClass = (option) => {
  const isActive = String(option.key) === String(activeKey.value)
  const isSelected = String(option.key) === String(props.modelValue)
  if (isUnavailable(option)) {
    return 'cursor-not-allowed text-gray-400 opacity-60 dark:text-gray-600'
  }
  if (isSelected) {
    return 'bg-brand-50 font-medium text-brand-700 dark:bg-brand-900/30 dark:text-brand-300'
  }
  if (isActive) {
    return 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-white/90'
  }
  return 'text-gray-700 dark:text-gray-300'
}

const setActive = (option) => {
  activeKey.value = option ? option.key : null
  if (!option) {
    return
  }
  nextTick(() => {
    if (!listbox.value) {
      return
    }
    // Escaped because a topic key is data; jsdom has no CSS.escape, hence the typeof guard.
    const raw = String(option.key)
    const selector = typeof CSS !== 'undefined' && CSS.escape ? CSS.escape(raw) : raw.replace(/"/g, '')
    const el = listbox.value.querySelector(`[data-key="${selector}"]`)
    // jsdom does not implement scrollIntoView, and this is cosmetic anyway.
    if (el && typeof el.scrollIntoView === 'function') {
      el.scrollIntoView({ block: 'nearest' })
    }
  })
}

const firstSelectable = () => selectableOptions.value[0] || null

const openList = () => {
  if (props.disabled || open.value) {
    return
  }
  open.value = true
  // The query starts empty so the full list is visible; the current choice stays legible because it
  // becomes the placeholder. Typing then narrows from everything rather than from one label.
  query.value = ''
  setActive(selectedOption.value && !isUnavailable(selectedOption.value) ? selectedOption.value : firstSelectable())
  emit('open')
}

const closeList = () => {
  if (!open.value) {
    return
  }
  open.value = false
  query.value = ''
  activeKey.value = null
  emit('close')
}

const select = (option) => {
  if (!option || isUnavailable(option)) {
    return
  }
  emit('update:modelValue', option.key)
  emit('change', option.key, option)
  closeList()
}

const clear = () => {
  emit('update:modelValue', '')
  emit('change', '', null)
  closeList()
  if (input.value) {
    input.value.focus()
  }
}

const onInput = (event) => {
  if (!open.value) {
    openList()
  }
  query.value = event.target.value
}

const onOptionHover = (option) => {
  if (!isUnavailable(option)) {
    activeKey.value = option.key
  }
}

const move = (delta) => {
  const list = selectableOptions.value
  if (!list.length) {
    activeKey.value = null
    return
  }
  const current = list.findIndex((o) => String(o.key) === String(activeKey.value))
  const next = current === -1 ? (delta > 0 ? 0 : list.length - 1) : (current + delta + list.length) % list.length
  setActive(list[next])
}

const onKeydown = (event) => {
  switch (event.key) {
    case 'ArrowDown':
      event.preventDefault()
      if (!open.value) {
        openList()
      } else {
        move(1)
      }
      break
    case 'ArrowUp':
      event.preventDefault()
      if (!open.value) {
        openList()
      } else {
        move(-1)
      }
      break
    case 'Home':
      if (open.value) {
        event.preventDefault()
        setActive(firstSelectable())
      }
      break
    case 'End':
      if (open.value) {
        event.preventDefault()
        setActive(selectableOptions.value[selectableOptions.value.length - 1] || null)
      }
      break
    case 'Enter':
      if (open.value) {
        event.preventDefault()
        select(activeOption.value)
      }
      break
    case 'Escape':
      if (open.value) {
        event.preventDefault()
        // Stopped here so Escape closes this list without also closing the dialog around it.
        event.stopPropagation()
        closeList()
      }
      break
    case 'Tab':
      closeList()
      break
    case 'Backspace':
    case 'Delete':
      // Backspace on an empty box clears the selection, the way a chip input does.
      if (props.clearable && !query.value && props.modelValue !== '' && props.modelValue !== null) {
        event.preventDefault()
        clear()
      }
      break
    default:
      break
  }
}

// A narrowed query can strip the active row out from under the cursor; re-anchor to the top.
watch(matched, () => {
  if (!open.value) {
    return
  }
  if (!activeOption.value || isUnavailable(activeOption.value)) {
    activeKey.value = (firstSelectable() || {}).key ?? null
  }
})

const onPointerDownOutside = (event) => {
  if (open.value && root.value && !root.value.contains(event.target)) {
    closeList()
  }
}

onMounted(() => document.addEventListener('mousedown', onPointerDownOutside))
onBeforeUnmount(() => document.removeEventListener('mousedown', onPointerDownOutside))

defineExpose({ focus: () => input.value && input.value.focus(), openList, closeList })
</script>
